/**
 * Estudo de Verba: regras puras da planilha (totais, taxas compartilhadas,
 * linhas vazias). Tax values are calculated through the Budget engine so the
 * Produção preview and its imported budget use the same rules.
 */
import { calculateProductionService } from "./budget-calculator";

export const FORNECEDOR_A_DEFINIR_ID = "FORNECEDOR_A_DEFINIR";
export const FORNECEDOR_A_DEFINIR_NAME = "Fornecedor a definir";

export const PAYMENT_FORMS = [
  { k: "SF", l: "Não selecionado", t: "Sem forma de pagamento" },
  {
    k: "A",
    l: "A – Pagamento direto pelo cliente",
    t: "Pagamento direto pelo cliente",
  },
  { k: "B", l: "B – Nota de débito", t: "Nota de débito" },
  { k: "C", l: "C – Custos de terceiros", t: "Custos de terceiros" },
  { k: "D", l: "D – Custos internos", t: "Custos internos" },
] as const;

export type PaymentForm = (typeof PAYMENT_FORMS)[number]["k"];

export const TAX_COLUMNS = [
  { k: "ts", l: "Taxa de Serviço", group: "Taxas", shared: true },
  { k: "iss", l: "ISS", group: "Taxas", shared: true },
  { k: "imp", l: "Imposto", group: "Impostos", shared: true },
  { k: "tmm", l: "Taxa MM", group: "Receita", shared: true },
  { k: "mk", l: "Markup/Over", group: "Receita", shared: false },
  { k: "imk", l: "Imposto s/ Markup/Over", group: "Receita", shared: false },
  { k: "com", l: "Comissão", group: "Receita", shared: false },
] as const;

export type TaxKey = (typeof TAX_COLUMNS)[number]["k"];
export type TaxMode = "%" | "R$";

export type TaxValue = {
  mode: TaxMode;
  v: string;
};

export const SHARED_TAX_KEYS: TaxKey[] = ["ts", "iss", "imp", "tmm"];
export const ITEM_TAX_KEYS: TaxKey[] = ["mk", "imk", "com"];

const SHARED_KEY_SET = new Set<TaxKey>(SHARED_TAX_KEYS);

export type DraftItem = {
  id: string;
  cat: string;
  svc: string;
  desc: string;
  qtd: string;
  dias: string;
  valor: string;
  forma: PaymentForm;
  forn: string;
  fornName: string;
  tx: Partial<Record<TaxKey, TaxValue>>;
  /** Serviço original, preservado só para o round-trip com o pré-orçamento. */
  source?: unknown;
};

export type DraftParams = Record<string, Partial<Record<TaxKey, TaxValue>>>;

export type DraftState = {
  items: DraftItem[];
  params: DraftParams;
};

export type TotalBucket = {
  tar: number;
  taxa: number;
  imposto: number;
  receita: number;
  geral: number;
  n: number;
};

export type KeyField = "cat" | "forma" | "forn";
export type TextField = "desc" | "qtd" | "dias" | "valor" | "svc";

let draftSeq = 0;

export function nextDraftId(): string {
  draftSeq += 1;
  return `draft-${draftSeq}`;
}

export function emptyDraft(): DraftState {
  return { items: [], params: {} };
}

export function isSharedTax(key: TaxKey): boolean {
  return SHARED_KEY_SET.has(key);
}

export function isFornecedorADefinir(item: Pick<DraftItem, "forn">): boolean {
  return !item.forn || item.forn === FORNECEDOR_A_DEFINIR_ID;
}

export function isSharedItem(item: Pick<DraftItem, "cat" | "forma">): boolean {
  return !!item.cat && item.forma !== "SF";
}

export function paramKey(
  item: Pick<DraftItem, "forma" | "forn" | "cat">
): string {
  const forn = item.forn || FORNECEDOR_A_DEFINIR_ID;
  return `${item.forma || "SF"}|${forn}|${item.cat || ""}`;
}

export function parseNumber(value: string | number | null | undefined): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  const normalized = raw.includes(",")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw;
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : 0;
}

export function formatBRL(value: number): string {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function blankItem(patch: Partial<DraftItem> = {}): DraftItem {
  return {
    id: patch.id || nextDraftId(),
    cat: "",
    svc: "",
    desc: "",
    qtd: "1",
    dias: "1",
    valor: "",
    forma: "SF",
    forn: FORNECEDOR_A_DEFINIR_ID,
    fornName: FORNECEDOR_A_DEFINIR_NAME,
    ...patch,
    tx: patch.tx ? { ...patch.tx } : {},
  };
}

export function createItem(
  draft: DraftState,
  patch: Partial<DraftItem> = {}
): { draft: DraftState; item: DraftItem } {
  const item = blankItem(patch);
  return { draft: { ...draft, items: [...draft.items, item] }, item };
}

export function lineTariff(
  item: Pick<DraftItem, "qtd" | "dias" | "valor">
): number {
  const daily = parseNumber(item.dias) || 1;
  return (
    Math.round(parseNumber(item.qtd) * daily * parseNumber(item.valor) * 100) /
    100
  );
}

export function effectiveTax(
  draft: DraftState,
  item: DraftItem,
  key: TaxKey
): TaxValue | undefined {
  if (isSharedTax(key) && isSharedItem(item)) {
    return draft.params[paramKey(item)]?.[key];
  }
  return item.tx[key];
}

export type LineCalc = TotalBucket & {
  markup: number;
  impostoSobreMarkup: number;
};

export function calcLine(draft: DraftState, item: DraftItem): LineCalc {
  const tar = lineTariff(item);
  const empty: LineCalc = {
    tar,
    taxa: 0,
    imposto: 0,
    receita: 0,
    geral: tar,
    n: 1,
    markup: 0,
    impostoSobreMarkup: 0,
  };
  if (item.forma === "SF") return empty;

  const calculated = calculateProductionService(draft, item);
  if (!calculated) return empty;
  const { taxData } = calculated;
  const ts = taxData.serviceTax.valueBRL;
  const iss = taxData.iss.valueBRL;
  const imp = taxData.tribute.valueBRL;
  const tmm = taxData.fee.valueBRL;
  const mk = taxData.markup.valueBRL;
  const com = taxData.commission.valueBRL;
  const imk = taxData.supplier_tribute.valueBRL;
  const taxa = ts + iss;
  const imposto = imp + imk;
  const receita = tmm + mk + com;

  return {
    tar,
    taxa,
    imposto,
    receita,
    geral: tar + taxa + imposto + receita,
    n: 1,
    markup: mk,
    impostoSobreMarkup: imk,
  };
}

function emptyBucket(): TotalBucket {
  return { tar: 0, taxa: 0, imposto: 0, receita: 0, geral: 0, n: 0 };
}

export function totalsBoard(draft: DraftState): {
  rows: Array<{
    key: PaymentForm;
    label: string;
    sub: string;
    onlyTariff: boolean;
    sums: TotalBucket;
  }>;
  geral: TotalBucket;
  tariffTotal: number;
} {
  const sums = new Map<PaymentForm, TotalBucket>();
  PAYMENT_FORMS.forEach((form) => sums.set(form.k, emptyBucket()));

  let tariffTotal = 0;
  draft.items.forEach((item) => {
    const calc = calcLine(draft, item);
    const bucket = sums.get(item.forma) || emptyBucket();
    bucket.n += 1;
    bucket.tar += calc.tar;
    bucket.taxa += calc.taxa;
    bucket.imposto += calc.imposto;
    bucket.receita += calc.receita;
    bucket.geral += calc.geral;
    sums.set(item.forma, bucket);
    tariffTotal += calc.tar;
  });

  const geral = emptyBucket();
  const ordered = [
    ...PAYMENT_FORMS.filter((form) => form.k !== "SF"),
    ...PAYMENT_FORMS.filter(
      (form) => form.k === "SF" && (sums.get("SF")?.n || 0) > 0
    ),
  ];
  const rows = ordered.map((form) => {
    const bucket = sums.get(form.k) || emptyBucket();
    (Object.keys(geral) as (keyof TotalBucket)[]).forEach((key) => {
      geral[key] += bucket[key];
    });
    return {
      key: form.k,
      label: form.k === "SF" ? "Sem forma de pagamento" : form.k,
      sub: form.k === "SF" ? "" : form.t,
      onlyTariff: form.k === "SF",
      sums: bucket,
    };
  });

  return { rows, geral, tariffTotal };
}

export function linkedCount(draft: DraftState, item: DraftItem): number {
  if (!isSharedItem(item)) return 0;
  const key = paramKey(item);
  return draft.items.filter((row) => isSharedItem(row) && paramKey(row) === key)
    .length;
}

export function pendenciaText(items: DraftItem[]): {
  text: string;
  tone: "empty" | "pending" | "ok";
} {
  if (!items.length) return { text: "Nenhum item lançado", tone: "empty" };
  const parts: string[] = [];
  const semForma = items.filter((item) => item.forma === "SF").length;
  const aDefinir = items.filter((item) => isFornecedorADefinir(item)).length;
  const semCategoria = items.filter((item) => !item.cat).length;
  const semServico = items.filter((item) => item.cat && !item.svc).length;
  if (semForma) parts.push(`${semForma} sem forma de pagamento`);
  if (aDefinir) parts.push(`${aDefinir} com fornecedor a definir`);
  if (semCategoria) parts.push(`${semCategoria} sem categoria`);
  if (semServico) parts.push(`${semServico} sem serviço`);
  if (!parts.length) return { text: "Todos os itens completos", tone: "ok" };
  return { text: parts.join(", "), tone: "pending" };
}

function cloneParams(params: DraftParams): DraftParams {
  const next: DraftParams = {};
  Object.entries(params).forEach(([key, value]) => {
    next[key] = { ...value };
  });
  return next;
}

function hasAny(taxes: Partial<Record<TaxKey, TaxValue>> | undefined): boolean {
  if (!taxes) return false;
  return SHARED_TAX_KEYS.some((key) => taxes[key] && taxes[key]!.v !== "");
}

function ownSharedTaxes(item: DraftItem): Partial<Record<TaxKey, TaxValue>> {
  const mine: Partial<Record<TaxKey, TaxValue>> = {};
  SHARED_TAX_KEYS.forEach((key) => {
    const tax = item.tx[key];
    if (tax && tax.v !== "") mine[key] = { ...tax };
  });
  return mine;
}

export function applyKeyChange(
  draft: DraftState,
  itemId: string,
  field: KeyField,
  value: string,
  options?: { categoryLabel?: string; supplierName?: string }
): { draft: DraftState; message: string | null } {
  const index = draft.items.findIndex((item) => item.id === itemId);
  if (index < 0) return { draft, message: null };

  const current = draft.items[index];
  const nextItem: DraftItem = {
    ...current,
    tx: { ...current.tx },
    [field]: value,
  };
  if (field === "cat" && value !== current.cat) nextItem.svc = "";
  if (field === "forn") {
    nextItem.fornName =
      value === FORNECEDOR_A_DEFINIR_ID
        ? FORNECEDOR_A_DEFINIR_NAME
        : options?.supplierName || current.fornName;
  }

  const params = cloneParams(draft.params);
  let message: string | null = null;
  const oldKey = paramKey(current);
  const newKey = paramKey(nextItem);

  if (oldKey !== newKey) {
    const mine =
      isSharedItem(current) && params[oldKey]
        ? { ...params[oldKey] }
        : ownSharedTaxes(current);
    SHARED_TAX_KEYS.forEach((key) => {
      delete nextItem.tx[key];
    });

    if (isSharedItem(nextItem)) {
      if (hasAny(params[newKey])) {
        const source = draft.items.findIndex(
          (row) => row.id !== nextItem.id && paramKey(row) === newKey
        );
        message =
          source >= 0
            ? `Taxas puxadas do item ${
                source + 1
              } (mesma forma de pagamento, fornecedor e categoria).`
            : "Taxas já parametrizadas para essa combinação foram aplicadas.";
      } else if (hasAny(mine)) {
        params[newKey] = { ...mine };
        const label = options?.categoryLabel || nextItem.cat;
        message = `Taxas salvas para ${label}: os próximos itens dessa combinação vão puxar estes valores.`;
      }
    } else if (hasAny(mine) && nextItem.forma !== "SF") {
      SHARED_TAX_KEYS.forEach((key) => {
        if (mine[key]) nextItem.tx[key] = { ...mine[key]! };
      });
    }
  }

  const items = draft.items.slice();
  items[index] = nextItem;
  return { draft: { items, params }, message };
}

export function setTextField(
  draft: DraftState,
  itemId: string,
  field: TextField,
  value: string
): DraftState {
  const index = draft.items.findIndex((item) => item.id === itemId);
  if (index < 0) return draft;
  const items = draft.items.slice();
  items[index] = { ...draft.items[index], [field]: value };
  return { ...draft, items };
}

export function setTaxValue(
  draft: DraftState,
  itemId: string,
  key: TaxKey,
  value: string
): DraftState {
  return writeTax(draft, itemId, key, { v: value });
}

export function setTaxMode(
  draft: DraftState,
  itemId: string,
  key: TaxKey,
  mode: TaxMode
): DraftState {
  return writeTax(draft, itemId, key, { mode });
}

function writeTax(
  draft: DraftState,
  itemId: string,
  key: TaxKey,
  patch: Partial<TaxValue>
): DraftState {
  const index = draft.items.findIndex((item) => item.id === itemId);
  if (index < 0) return draft;
  const item = draft.items[index];
  const current = effectiveTax(draft, item, key);
  const next: TaxValue = {
    mode: patch.mode ?? current?.mode ?? "%",
    v: patch.v ?? current?.v ?? "",
  };

  if (isSharedTax(key) && isSharedItem(item)) {
    const keyId = paramKey(item);
    return {
      ...draft,
      params: {
        ...draft.params,
        [keyId]: { ...(draft.params[keyId] || {}), [key]: next },
      },
    };
  }

  const tx = { ...item.tx };
  if (next.v === "" && patch.mode === undefined) delete tx[key];
  else tx[key] = next;
  const items = draft.items.slice();
  items[index] = { ...item, tx };
  return { ...draft, items };
}

export function deleteItem(draft: DraftState, itemId: string): DraftState {
  return {
    ...draft,
    items: draft.items.filter((item) => item.id !== itemId),
  };
}

export function moveItemBy(
  draft: DraftState,
  itemId: string,
  delta: -1 | 1
): DraftState {
  const from = draft.items.findIndex((item) => item.id === itemId);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= draft.items.length) return draft;
  const items = draft.items.slice();
  const [row] = items.splice(from, 1);
  items.splice(to, 0, row);
  return { ...draft, items };
}

export function reorderDraft(
  draft: DraftState,
  activeId: string,
  overId: string,
  place: "before" | "after"
): DraftState {
  if (!overId || activeId === overId) return draft;
  const items = draft.items.slice();
  const from = items.findIndex((item) => item.id === activeId);
  let to = items.findIndex((item) => item.id === overId);
  if (from < 0 || to < 0) return draft;
  const [row] = items.splice(from, 1);
  if (from < to) to -= 1;
  items.splice(place === "after" ? to + 1 : to, 0, row);
  return { ...draft, items };
}
